/**
 * Meta Attribution Capture & Hashing Utilities
 *
 * Captures UTMs, fbclid, _fbp, _fbc from URL/cookies on first load,
 * persists in sessionStorage, and provides SHA-256 hashing for Advanced Matching.
 *
 * Used by metaPixel.ts and form submissions.
 */

const STORAGE_KEY = "rea_meta_attribution";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface AttributionData {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
  fbp: string | null;
  fbc: string | null;
  landing_path: string | null;
  first_visit_at: string | null;
}

/* ------------------------------------------------------------------ */
/*  SHA-256 Hashing (Web Crypto API — browser-native, no deps)        */
/* ------------------------------------------------------------------ */

export async function sha256(input: string): Promise<string> {
  const normalized = input.trim().toLowerCase();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Normalize phone to E.164 format for Meta hashing.
 * Removes spaces, dashes; ensures +56 prefix for Chilean numbers.
 */
export function normalizePhoneE164(phone: string): string {
  if (!phone) return "";
  let clean = phone.replace(/[\s\-\(\)]/g, "");
  // If starts with 56 without +, add it
  if (clean.startsWith("56") && !clean.startsWith("+")) {
    clean = "+" + clean;
  }
  // If starts with 9 (Chilean mobile without prefix)
  if (clean.startsWith("9") && clean.length === 9) {
    clean = "+56" + clean;
  }
  return clean;
}

/* ------------------------------------------------------------------ */
/*  Cookie reader                                                      */
/* ------------------------------------------------------------------ */

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/* ------------------------------------------------------------------ */
/*  Capture & Persist Attribution                                      */
/* ------------------------------------------------------------------ */

/**
 * Captures attribution data from URL params and cookies.
 * Only captures on first call per session (persisted in sessionStorage).
 * Should be called once at app boot.
 */
export function captureAttribution(): AttributionData {
  if (typeof window === "undefined") {
    return emptyAttribution();
  }

  // Check if already captured this session
  const existing = sessionStorage.getItem(STORAGE_KEY);
  if (existing) {
    try {
      return JSON.parse(existing) as AttributionData;
    } catch {
      // Corrupted, recapture
    }
  }

  const params = new URLSearchParams(window.location.search);

  const data: AttributionData = {
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_content: params.get("utm_content"),
    utm_term: params.get("utm_term"),
    fbclid: params.get("fbclid"),
    fbp: getCookie("_fbp"),
    fbc: getCookie("_fbc") || buildFbc(params.get("fbclid")),
    landing_path: window.location.pathname + window.location.search,
    first_visit_at: new Date().toISOString(),
  };

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  return data;
}

/**
 * Returns current attribution data (from sessionStorage).
 * If not yet captured, captures now.
 */
export function getAttribution(): AttributionData {
  if (typeof window === "undefined") return emptyAttribution();

  const existing = sessionStorage.getItem(STORAGE_KEY);
  if (existing) {
    try {
      return JSON.parse(existing) as AttributionData;
    } catch {
      // fallthrough
    }
  }
  return captureAttribution();
}

/**
 * Build _fbc from fbclid if cookie doesn't exist.
 * Format: fb.1.{timestamp}.{fbclid}
 */
function buildFbc(fbclid: string | null): string | null {
  if (!fbclid) return null;
  return `fb.1.${Date.now()}.${fbclid}`;
}

function emptyAttribution(): AttributionData {
  return {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_content: null,
    utm_term: null,
    fbclid: null,
    fbp: null,
    fbc: null,
    landing_path: null,
    first_visit_at: null,
  };
}

/* ------------------------------------------------------------------ */
/*  Event ID Generation                                                */
/* ------------------------------------------------------------------ */

/**
 * Generate a UUID v4 for event deduplication (Pixel ↔ CAPI).
 * Uses crypto.randomUUID() when available, falls back to manual generation.
 */
export function generateEventId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
